import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Batch, ACTIVE_BATCH_STATUSES } from 'src/app/models/batch';
import { BatchService } from 'src/app/services/batch.service';

const POLL_INTERVAL_MS = 3000;

@Component({
  selector: 'app-batch-progress',
  templateUrl: './batch-progress.component.html',
  styleUrls: ['./batch-progress.component.scss']
})
export class BatchProgressComponent implements OnInit, OnChanges, OnDestroy {
  // when false, parent component handles polling itself
  @Input() selfPoll = true;
  // Display-only aggregate progress (0-100). Used when `selfPoll` drives polling
  @Input() progress = 0;
  @Input() batch?: Batch;

  intervalId: any;

  @Output() refresh = new EventEmitter<any>();

  constructor(
    private batchService: BatchService
  ) {
  }

  ngOnInit() {
    this.checkPolling();
  }

  // the parent replaces the batch object on reanalyze (optimistic PENDING),
  // which restarts polling after it was previously stopped
  ngOnChanges(_changes: SimpleChanges) {
    if (!this.selfPoll) return;
    this.checkPolling();
  }

  private checkPolling() {
    const isActive = !!this.batch && ACTIVE_BATCH_STATUSES.includes(this.batch.status);
    if (!this.selfPoll || !isActive) {
      this.stopPolling();
      if (this.batch) {
        this.score(this.batch);
      }
      return;
    }
    if (this.intervalId) return; // already polling

    void this.update();
    this.intervalId = setInterval(() => {
      void this.update();
    }, POLL_INTERVAL_MS);
  }

  private stopPolling() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  async update() {
    if (this.batch && this.batch._id) {
      const batchId = this.batch._id;
      try {
        const [batch, { progress }] = await Promise.all([
          this.batchService.retrieve(batchId),
          this.batchService.retrieveProgress(batchId),
        ]);

        this.batch = batch;
        this.progress = Math.round(100 * progress);
        if (this.progress >= 100) {
          this.stopPolling();
          this.onRefresh();
        }
      } catch {
        // keep polling, the next tick will retry
      }
    }
  }

  // In selfPoll=false, the parent supplies the progress
  score(batch: Batch | undefined) {
    if (!this.selfPoll) return;
    if (this.batch && this.batch.documents > 0) {
      this.progress = Math.round(100 * ((this.batch?.report?.ok ?? 0) + (this.batch?.report?.error ?? 0) + (this.batch?.report?.warning ?? 0)) / (this.batch?.documents ?? 1))
    } else if (this.batch && this.batch.documents == 0) {
      this.progress = 100;
    }
  }

  onRefresh() {
    this.refresh.emit(this.batch)
  }
}
